import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedPage from '../components/AnimatedPage';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Sparkles, Heart, Leaf, Brain } from 'lucide-react';

const WellnessServices = () => {
  const [servicesRef, servicesVisible] = useScrollAnimation(0.1);

  const services = [
    {
      category: "BIOIDENTICAL HORMONE REPLACEMENT THERAPY",
      title: "Bioidentical Hormone Replacement Therapy",
      description: "A personalized treatment that uses molecularly identical compounds to replicate the body's natural hormones, easing the symptoms of hormonal decline and optimizing long-term wellness.",
      icon: Heart,
      link: "/services/wellness-services/purepell",
      bgImage: "https://images.pexels.com/photos/8313181/pexels-photo-8313181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      category: "AESTHETIC SERVICES",
      title: "Aesthetic Services",
      description: "Advanced aesthetic treatments including body contouring and wellness-focused beauty solutions.",
      icon: Sparkles,
      link: "/services/wellness-services/aesthetic-services",
      bgImage: "https://images.pexels.com/photos/37229298/pexels-photo-37229298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      category: "SUPPLEMENT SUPPORT",
      title: "Supplement Support",
      description: "Professional-grade supplements and recommendations for optimal health.",
      icon: Leaf,
      link: "/services/wellness-services/fullscript",
      bgImage: "https://images.pexels.com/photos/7615558/pexels-photo-7615558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      category: "MINDFULNESS",
      title: "Mindfulness",
      description: "Guided meditation sessions designed to promote relaxation, reduce stress, and enhance overall well-being.",
      icon: Brain,
      link: "/services/wellness-services/mindfulness",
      bgImage: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  ];

  return (
    <AnimatedPage className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-stone-50 via-stone-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Wellness Services
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-8"></div>
            
            {/* Content Under Heading */}
            <div className="max-w-4xl mx-auto">
              <p className="text-base text-stone-700 leading-relaxed text-left">
                In recent years, wellness and functional medicine have moved to the forefront of patient care. 
                At Crofton Family Practice, we seek to bring these innovative approaches to our patients while 
                continuing to honor the foundation of traditional allopathic medicine. The MDVIP wellness model 
                serves as the perfect companion to this philosophy. By utilizing its advanced diagnostic testing, 
                we can translate your unique results into a highly personalized wellness program that takes your 
                healthcare to the next level. The wellness services are separately priced and not restricted to MDVIP membership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Wellness Services Section */}
      <section ref={servicesRef} className="pt-8 pb-20 bg-gradient-to-br from-stone-50 via-amber-50/20 to-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Services Grid - 4 Items */}
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-1000 ${servicesVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Link 
                  key={service.link} 
                  to={service.link}
                  className="group block h-full"
                >
                  <div className="relative h-[340px] rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    {/* Background Image with Overlay */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                      style={{
                        backgroundImage: `url(${service.bgImage})`
                      }}
                    ></div>
                    
                    {/* Gradient Overlay - Lighter Beige/Cream */}
                    <div className="absolute inset-0 bg-gradient-to-b from-stone-100/85 via-stone-50/90 to-amber-50/95"></div>
                    
                    {/* Content Container */}
                    <div className="relative h-full flex flex-col justify-between p-5">
                      {/* Top Content */}
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-stone-900 mb-2 leading-tight">
                          {service.title}
                        </h3>
                      </div>
                      
                      {/* Icon at Bottom - Large Dark Circle */}
                      <div className="flex justify-center">
                        <div className="w-20 h-20 rounded-full bg-stone-800 flex items-center justify-center border-2 border-amber-500 group-hover:bg-amber-600 group-hover:border-amber-500 group-hover:scale-110 transition-all duration-300 shadow-lg">
                          <Icon className="h-9 w-9 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </AnimatedPage>
  );
};

export default WellnessServices;
